import { useCallback, memo, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Cross, cn } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import { useDeps } from '@Contexts/DI';
import styles from './ModelViewerAr.module.css';

import type { AR } from '@Types/AR';
import type { FC, HTMLAttributes } from 'react';

export interface ModelViewerArProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  ar: AR;
  view?: 'gallery' | 'default';
  idViewerModel: string;
  withCross?: boolean;
}

const ModelViewerAr: FC<ModelViewerArProps> = (props) => {
  const { className, ar, view = 'default', withCross = true, idViewerModel, ...restProps } = props;
  const { isDesktopM, isMobileM } = useMedias();
  const { analytics } = useDeps();
  const { t } = useTranslation();

  const qrcodeSize = useMemo(() => {
    if (view === 'gallery') {
      if (isDesktopM) return 110;
    }

    return 160;
  }, [isDesktopM, view]);

  const handleClose = useCallback(() => {
    PageProductStore.changeAr(idViewerModel, false);

    analytics.dispatchEvent('card.showAr', {
      isShowAr: false,
    });
  }, [analytics, idViewerModel]);

  return (
    <div {...restProps} className={cn(styles.modelViewer, className)}>
      {!isMobileM && (
        <div className={cn(styles.wrapper, { [styles.galleryView]: view === 'gallery' })}>
          <div className={styles.textContent}>
            <div className={styles.title}>{t('show-in-room')}</div>
            <div className={styles.text}>{t('point-camera-at-QR')}</div>
            <div className={styles.notice}>{t('with-support-AR')}</div>
          </div>
          <div className={styles.content}>
            <div className={cn(styles.step, styles.stepQr)}>
              <div className={cn(styles.image, styles.qrContent)}>
                <div className={styles.qr}>
                  <QRCodeSVG value={ar.qrcode} size={qrcodeSize} />
                </div>
                <span className={styles.count}>1</span>
              </div>
              <div className={styles.stepText}>{t('scan-QR')}</div>
            </div>
            <div className={cn(styles.step, styles.stepAr)}>
              <div className={cn(styles.image, styles.arContent)}>
                <span className={styles.count}>2</span>
              </div>
              <div className={styles.stepText}>{t('place-furniture-in-AR')}</div>
            </div>
          </div>
        </div>
      )}
      {withCross && (
        <Cross className={styles.close} size={isDesktopM ? 's' : 'm'} onClick={handleClose} />
      )}
    </div>
  );
};

export default memo(ModelViewerAr);
